import React, { useState } from 'react'
import { ZoomIn, ZoomOut, RotateCw, Download } from 'lucide-react'

interface Highlight {
  id: string
  text: string
  startIndex: number
  length: number
  page: number
}

interface ImageViewerProps {
  imageUrl: string
  fileName?: string
  highlights?: Highlight[]
  onHighlightClick?: (highlight: Highlight) => void
  className?: string
}

export const ImageViewer: React.FC<ImageViewerProps> = ({
  imageUrl,
  fileName = 'image',
  highlights = [],
  onHighlightClick,
  className = ''
}) => {
  const [zoom, setZoom] = useState(75)  // Start at 75% for better fit
  const [rotation, setRotation] = useState(0)
  const [isOverlayVisible, setIsOverlayVisible] = useState(true)

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 25, 200))
  }

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 25, 50))
  }

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360)
  }

  const handleDownload = () => {
    const link = document.createElement('a')
    link.href = imageUrl
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  if (!imageUrl) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-100">
        <div className="text-center">
          <p className="text-gray-500">No image available</p>
        </div>
      </div>
    )
  }

  return (
    <div className={`relative h-full flex flex-col bg-gray-100 ${className}`}>
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-gray-200">
        <div className="flex items-center gap-2">
          <button
            onClick={handleZoomOut}
            className="p-2 hover:bg-gray-100 rounded transition-colors"
            title="Zoom Out"
            disabled={zoom <= 50}
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          <span className="text-sm font-medium min-w-[60px] text-center">
            {zoom}%
          </span>
          <button
            onClick={handleZoomIn}
            className="p-2 hover:bg-gray-100 rounded transition-colors"
            title="Zoom In"
            disabled={zoom >= 200}
          >
            <ZoomIn className="w-4 h-4" />
          </button>
          <div className="h-6 w-px bg-gray-300 mx-2" />
          <button
            onClick={handleRotate}
            className="p-2 hover:bg-gray-100 rounded transition-colors"
            title="Rotate"
          >
            <RotateCw className="w-4 h-4" />
          </button>
        </div>
        <button
          onClick={handleDownload}
          className="p-2 hover:bg-gray-100 rounded transition-colors flex items-center gap-2"
          title="Download"
        >
          <Download className="w-4 h-4" />
          <span className="text-sm">Download</span>
        </button>
      </div>

      {/* Image Container */}
      <div className="flex-1 overflow-auto p-4 flex items-center justify-center bg-gray-50">
        <div className="relative w-full h-full flex items-center justify-center">
          <img
            src={imageUrl}
            alt={fileName}
            style={{
              transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
              transition: 'transform 0.2s ease-in-out',
              maxWidth: '100%',
              maxHeight: '100%',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain'
            }}
            className="shadow-lg"
          />
        </div>
      </div>

      {/* Highlight info overlay */}
      {highlights.length > 0 && isOverlayVisible && (
        <div className="absolute top-20 right-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3 max-w-xs shadow-lg z-10">
          {/* Close button */}
          <button
            onClick={() => setIsOverlayVisible(false)}
            className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close overlay"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="flex items-center gap-2 mb-2 pr-6">
            <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
            <div className="text-sm font-semibold text-gray-800">
              Evidence Located
            </div>
          </div>
          <div className="text-xs text-gray-600 mb-1">
            Found in this image
          </div>
          <div className="mt-2 text-xs text-gray-700 bg-white p-2 rounded border">
            <div className="font-medium mb-1">Evidence:</div>
            <div className="italic">
              "{highlights[0].text.substring(0, 200)}{highlights[0].text.length > 200 ? '...' : ''}"
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ImageViewer
